// Welle 131 — Maske „Stammdaten (DB 2)": zeigt die GROSSE Tabelle (v_alle_vertraege + Banken), schreibt aber in die
// richtige KLEINE Tabelle je Art (vertraege, versicherungen, banken, steuernr, darlehen). Mietverträge nur lesen.
// Klick auf eine Zeile → diese Zeile bearbeiten (z. B. fehlende Zählernummer nachtragen); „Neu" → je Art eigene Felder.
// Vorschau zeigt, was Rename-me daraus macht; „Speichern + Neu analysieren" analysiert die Datei danach neu.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { KATS, KAT_AUS_TABELLE, LABEL, SD_API, sdJson, sdPost, sdPut, wirksamText } from "./stammdatenApi";
import OrdnerWahl from "./OrdnerWahl";
import "./Stammdaten.css";

const N = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const LEER = {};          // feste Referenz — ein neues {} je Render würde die Lade-Effekte endlos neu auslösen

export default function StammdatenAnlegenDialog({ dateiname, vorbelegung = LEER, startKat = "vertraege", onClose, onGespeichert }) {
  const [kat, setKat] = useState(startKat);
  const [meta, setMeta] = useState(null);            // {spalten, pflicht, nur_lesen, zeilen}
  const [quelle, setQuelle] = useState(null);
  const [objekt, setObjekt] = useState((vorbelegung.prefix || "").replace(/^#/, ""));
  const [q, setQ] = useState("");
  const [uebersicht, setUebersicht] = useState([]);
  const [modus, setModus] = useState("neu");          // neu | bearbeiten
  const [rowId, setRowId] = useState(null);
  const [form, setForm] = useState({});
  const [vorschau, setVorschau] = useState(null);
  const [fehler, setFehler] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [ziel, setZiel] = useState(null);             // {kat, id} — Zeile aus der großen Tabelle, wird nach dem Laden übernommen
  const [ordnerWahl, setOrdnerWahl] = useState(false); // Zielpfad des Vertrags wählen
  const nummern = useMemo(() => (vorbelegung.nummern || []).map(N).filter((n) => n.length >= 4), [vorbelegung]);

  useEffect(() => { sdJson(`${SD_API}/quelle`).then((d) => setQuelle(d.schalter)).catch(() => {}); }, []);

  const ladeUebersicht = useCallback(async () => {
    try {
      const d = await sdJson(`${SD_API}/uebersicht?objekt=${encodeURIComponent(objekt)}&q=${encodeURIComponent(q)}&limit=600`);
      setUebersicht(d.zeilen || []);
    } catch (e) { setFehler(String(e.message || e)); }
  }, [objekt, q]);
  useEffect(() => { const t = setTimeout(ladeUebersicht, 250); return () => clearTimeout(t); }, [ladeUebersicht]);

  const leererForm = useCallback((k, spalten) => {
    const f = {};
    spalten.forEach((s) => { f[s] = ""; });
    const p = (vorbelegung.prefix || "").replace(/^#/, "");
    if (k === "vertraege") Object.assign(f, { objektid: p, lieferant: vorbelegung.absender || "", vetragsnummer: vorbelegung.vertragsnummer || "",
      zaehlernummer: vorbelegung.zaehlernummer || "", kundennummer: vorbelegung.kundennummer || "",
      ...(vorbelegung.vertagspartner ? { vertagspartner: vorbelegung.vertagspartner } : {}),     // Welle 132: aus dem Firmenwechsel
      ...(vorbelegung.art ? { art: vorbelegung.art } : {}) });
    if (k === "versicherungen") Object.assign(f, { objektnr: /^\d{3}/.test(p) ? p : "", rename_praefix: /^\d{3}/.test(p) ? "" : p,
      gesellschaft: vorbelegung.absender || "", vers_nr: vorbelegung.versicherungsnummer || "" });
    if (k === "iban") Object.assign(f, { rename_praefix: p, iban: vorbelegung.iban || "", bank_kuerzel: "" });
    if (k === "steuernummer") Object.assign(f, { suchbegriff: p, steuernr: vorbelegung.steuernummer || "" });
    if (k === "darlehen") Object.assign(f, { rename_praefix: p, darlehens_nr: vorbelegung.darlehensnummer || "" });
    return f;
  }, [vorbelegung]);

  useEffect(() => {
    setMeta(null); setVorschau(null); setFehler(""); setOk(""); setModus("neu"); setRowId(null);
    sdJson(`${SD_API}/${kat}`).then((d) => { setMeta({ ...d, _kat: kat }); setForm(leererForm(kat, d.spalten)); })
      .catch((e) => setFehler(String(e.message || e)));
  }, [kat, leererForm]);

  // gewählte Zeile übernehmen, sobald die Daten ihrer Art geladen sind
  useEffect(() => {
    if (!ziel || !meta || ziel.kat !== kat || meta._kat !== kat) return;     // erst wenn die Daten DIESER Art geladen sind
    const row = (meta.zeilen || []).find((r) => String(r.id) === String(ziel.id));
    if (row) {
      setModus("bearbeiten"); setRowId(row.id); setVorschau({ wirksam: row._wirksam });
      setForm(Object.fromEntries(meta.spalten.map((s) => [s, row[s] ?? ""])));
    }
    setZiel(null);
  }, [ziel, meta, kat]);

  const zeileWaehlen = (z) => {
    const k = KAT_AUS_TABELLE[z.quelle_tabelle];
    if (!k) return;
    setZiel({ kat: k, id: z.quelle_id });
    if (k !== kat) setKat(k);
  };

  const pruefen = async () => {
    setFehler(""); setOk("");
    try {
      if (modus === "neu") setVorschau(await sdPost(`/${kat}?dry_run=1`, form));
    } catch (e) { setFehler(String(e.message || e)); setVorschau(null); }
  };

  const speichern = async (neuAnalysieren) => {
    setBusy(true); setFehler(""); setOk("");
    try {
      if (modus === "neu") {
        const d = await sdPost(`/${kat}?dry_run=0`, form);
        setOk(`Angelegt in ${d.tabelle} (${wirksamText(d.wirksam)}).`);
      } else {
        await sdPut(`/${kat}/${rowId}`, form);
        setOk("Gespeichert.");
      }
      ladeUebersicht();
      onGespeichert && onGespeichert({ kat, neuAnalysieren });
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const istTreffer = (z) => nummern.length && nummern.some((n) =>
    [z.vertragsnummer, z.kundennummer, z.zaehlernummer, z.vertrgskonto, z.marktlocation].some((v) => v && N(v).includes(n)));
  const spalten = meta?.spalten || [];
  const kq = quelle ? quelle[kat] : null;
  const felder = kat === "iban" && modus === "neu" ? [...spalten.filter((s) => s !== "suchbegriff"), "bank_kuerzel"] : spalten;

  return (
    <div className="sd-overlay" onClick={onClose}>
      <div className="sd-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="sd-kopf">
          <div>
            <h3>Stammdaten (DB 2) — Verträge, Versicherungen, Banken …</h3>
            {dateiname && <div className="sd-klein">Datei: {dateiname}</div>}
            {vorbelegung.hinweis && <div className="sd-klein">{vorbelegung.hinweis}</div>}
          </div>
          <button className="sd-btn" onClick={onClose}>✕</button>
        </div>

        <div className="sd-leiste">
          <input className="sd-in" style={{ width: 120 }} placeholder="Objekt (z. B. 023)" value={objekt} onChange={(e) => setObjekt(e.target.value.trim())} />
          <input className="sd-in" style={{ flex: "1 1 260px" }} placeholder="Suchen in der großen Tabelle (Nummer, Lieferant, Name …)" value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="sd-klein">{uebersicht.length} Zeilen · grün = Nummer aus der Datei · Klick = bearbeiten</span>
        </div>
        <div className="sd-tab-wrap">
          <table className="sd-tab">
            <thead><tr><th>Art</th><th>Objekt</th><th>Schlüssel</th><th>Partner</th><th>Vertrags-/Vers.-Nr. / IBAN</th><th>Kunden-Nr.</th><th>Zähler</th><th>Status</th><th>Präfix (Rename)</th></tr></thead>
            <tbody>
              {uebersicht.map((z) => (
                <tr key={`${z.quelle_tabelle}-${z.quelle_id}`}
                  className={["klickbar", istTreffer(z) ? "treffer" : "", modus === "bearbeiten" && KAT_AUS_TABELLE[z.quelle_tabelle] === kat && String(rowId) === String(z.quelle_id) ? "gewaehlt" : "",
                    /gekündigt|beendet/.test(z.status || "") ? "gekuendigt" : ""].join(" ")}
                  onClick={() => zeileWaehlen(z)} title={KAT_AUS_TABELLE[z.quelle_tabelle] ? "Zeile bearbeiten" : ""}>
                  <td>{z.art}{z.unterart ? ` · ${z.unterart}` : ""}</td><td>{z.objekt_id}</td><td>{z.schluessel}</td>
                  <td>{z.vertragspartner}</td><td>{z.vertragsnummer}</td><td>{z.kundennummer}</td><td>{z.zaehlernummer}</td>
                  <td>{z.status}</td><td>{z.rename_praefix}</td>
                </tr>
              ))}
              {!uebersicht.length && <tr><td colSpan={9} className="sd-klein">Keine Zeilen — Objekt/Suche anpassen.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="sd-chips" style={{ marginTop: 12 }}>
          {KATS.map((k) => (
            <button key={k.kat} type="button" className={`sd-chip ${kat === k.kat ? "aktiv" : ""}`} onClick={() => setKat(k.kat)}>{k.titel}</button>
          ))}
          {kq && !["db2", "fallback", "vorrang"].includes(kq) &&
            <span className="sd-badge warn" title="Umschalter auf der STRG-Seite">Rename-me liest {KATS.find((k) => k.kat === kat)?.titel} noch aus DB 1</span>}
        </div>

        {meta?.nur_lesen ? (
          <div className="sd-klein">Mietverträge werden in der Mietverwaltung gepflegt — hier nur zur Ansicht (Rename-me erkennt Mietvertrags- und Mieternummern daraus).</div>
        ) : meta && (
          <>
            <div className="sd-leiste">
              <b>{modus === "neu" ? `Neu: ${KATS.find((k) => k.kat === kat)?.titel}` : `Bearbeiten: Zeile ${rowId}`}</b>
              {modus === "bearbeiten" && <button className="sd-btn" onClick={() => { setModus("neu"); setRowId(null); setForm(leererForm(kat, spalten)); setVorschau(null); }}>stattdessen neu anlegen</button>}
              <span className="sd-klein">Geschrieben wird in die kleine Tabelle „{KATS.find((k) => k.kat === kat)?.tabelle}" — die große Tabelle zeigt es danach an.</span>
            </div>
            <div className="sd-form">
              {felder.map((s) => s === "zielpfad" ? (
                <div key={s} className="sd-feld sd-zielpfad">
                  <span>{LABEL[s] || s} — Dateien dieses Vertrags landen direkt hier (vor KI und Regeln)</span>
                  <div className="sd-zielpfad-feld">
                    <input className="sd-in" readOnly value={form[s] ?? ""} placeholder="leer = Ziel wie bisher (KI/Regel)"
                      title={form[s] || ""} onClick={() => setOrdnerWahl(true)} />
                    <button type="button" className="sd-btn sd-zielpfad-btn" onClick={() => setOrdnerWahl(true)}>📁 Zielpfad angeben</button>
                  </div>
                </div>
              ) : (
                <label key={s} className={`sd-feld ${(meta.pflicht || []).includes(s) || (s === "bank_kuerzel" && !form.suchbegriff) ? "pflicht" : ""}`}>
                  <span>{LABEL[s] || s}</span>
                  <input className="sd-in" value={form[s] ?? ""} onChange={(e) => { setForm({ ...form, [s]: e.target.value }); setVorschau(null); }} />
                </label>
              ))}
            </div>
            {vorschau?.wirksam && <div className="sd-vorschau">Rename-me erkennt daraus: <b>{wirksamText(vorschau.wirksam)}</b>
              {vorschau.zeile?.suchbegriff ? <> · Schlüssel <code>{vorschau.zeile.suchbegriff}</code></> : null}</div>}
            {fehler && <div className="sd-fehler">{fehler}</div>}
            {ok && <div className="sd-ok">{ok}</div>}
            <div className="sd-leiste" style={{ marginTop: 10, justifyContent: "flex-end" }}>
              {modus === "neu" && <button className="sd-btn" disabled={busy} onClick={pruefen}>Vorschau</button>}
              <button className="sd-btn" disabled={busy || (modus === "neu" && !vorschau)} onClick={() => speichern(false)}>Speichern</button>
              <button className="sd-btn primary" disabled={busy || (modus === "neu" && !vorschau)} onClick={() => speichern(true)}>Speichern + Neu analysieren</button>
            </div>
          </>
        )}
      </div>
      {ordnerWahl && (
        <OrdnerWahl wert={form.zielpfad || ""} onClose={() => setOrdnerWahl(false)}
          onWahl={(p) => { setForm({ ...form, zielpfad: p }); setVorschau(null); setOrdnerWahl(false); }} />
      )}
    </div>
  );
}
